import type { ParcelDto } from "@babackapp/private-api-client/types";
import {
  hasEstimatedDeliveryDateDayOnly,
  hasEstimatedDeliveryDateWindowOnSameDay,
  hasIdenticalDeliveryTimes,
} from "@babackapp/ui/lib/date-window";
import { format, formatDate } from "date-fns";
import { tLayout } from "../../translations";
import type { EstimatedDeliveryDateBlockProps } from "./types";
import {
  findArrivedEvent,
  findDeliveredEvent,
  getDateFnsLocale,
} from "./utils";

const createEstimationDeliveryDateMock =
  (): ParcelDto["estimatedDeliveryDateWindow"] => {
    const today = new Date();
    return {
      exactDeliveryDay: new Date(
        today.getTime() + 5 * 24 * 60 * 60 * 1000,
      ).getTime(),
      minimumDeliveryDate: new Date(
        today.getTime() + 3 * 24 * 60 * 60 * 1000,
      ).getTime(),
      maximumDeliveryDate: new Date(
        today.getTime() + 7 * 24 * 60 * 60 * 1000,
      ).getTime(),
    };
  };

export function EstimatedDeliveryDateWebRenderer({
  parcel,
  locale,
  defaultLocale,
}: EstimatedDeliveryDateBlockProps) {
  const t = tLayout(locale, defaultLocale);
  const dateWindow =
    parcel?.estimatedDeliveryDateWindow || createEstimationDeliveryDateMock();

  if (!dateWindow) {
    return null;
  }

  // Check for delivered status
  if (parcel) {
    const deliveredEvent = findDeliveredEvent(parcel as ParcelDto);
    if (deliveredEvent) {
      const customerLocale = (parcel as ParcelDto).customerLocale || "en";
      const dateFnsLocale = getDateFnsLocale(customerLocale);
      const deliveredDate = format(deliveredEvent.createdAt, "d MMMM yyyy", {
        locale: dateFnsLocale,
      });
      return (
        <p className="text-center text-xs font-medium">
          {t("tracking.shared.deliveredOn")} {deliveredDate}
        </p>
      );
    }

    // Check for arrived status (pickup point, etc.)
    const arrivedEvent = findArrivedEvent(parcel as ParcelDto);
    if (arrivedEvent) {
      return null;
    }
  }

  const isDayOnly = hasEstimatedDeliveryDateDayOnly(dateWindow);
  if (isDayOnly && dateWindow.exactDeliveryDay) {
    const dateStr = formatDate(dateWindow.exactDeliveryDay, "dd/MM/yyyy");
    return (
      <p className="text-center text-xs">
        {t("tracking.shared.estimatedDeliveryDate")}&nbsp; {dateStr}
      </p>
    );
  }

  const isSameDayWindow = hasEstimatedDeliveryDateWindowOnSameDay(dateWindow);
  if (
    isSameDayWindow &&
    dateWindow.minimumDeliveryDate &&
    dateWindow.maximumDeliveryDate
  ) {
    const day = formatDate(dateWindow.minimumDeliveryDate, "dd/MM/yyyy");

    if (hasIdenticalDeliveryTimes(dateWindow)) {
      return (
        <p className="text-center text-xs">
          {t("tracking.shared.estimatedDeliveryDate")}&nbsp; {day}
        </p>
      );
    }

    const minStr = formatDate(dateWindow.minimumDeliveryDate, "HH:mm");
    const maxStr = formatDate(dateWindow.maximumDeliveryDate, "HH:mm");

    return (
      <p className="text-center text-xs">
        {t("tracking.shared.estimatedDeliveryDate")}&nbsp;
        {day} {minStr} - {maxStr}
      </p>
    );
  }

  if (dateWindow.minimumDeliveryDate && dateWindow.maximumDeliveryDate) {
    const minStr = formatDate(dateWindow.minimumDeliveryDate, "dd/MM/yyyy");
    const maxStr = formatDate(dateWindow.maximumDeliveryDate, "dd/MM/yyyy");

    return (
      <p className="text-center text-xs">
        {t("tracking.shared.estimatedDeliveryDate")}&nbsp;
        {minStr} - {maxStr}
      </p>
    );
  }

  if (dateWindow.exactDeliveryDay) {
    const dateStr = formatDate(dateWindow.exactDeliveryDay, "dd/MM/yyyy");
    return (
      <p className="text-center text-xs">
        {t("tracking.shared.estimatedDeliveryDate")}&nbsp; {dateStr}
      </p>
    );
  }

  return null;
}
